import React, { ReactElement, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDebounce } from "react-use";

import SearchIcon from "@mui/icons-material/Search";

import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import { getAllErrors } from "redux/handlers/errorHandler";

import { useAppDispatch } from "hooks/reduxHook";

import { IErrorData } from "types/api/errorsApiInterface";

const Errors = (): ReactElement => {
 const dispatch = useAppDispatch();
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("lg"));

 const searchRef = useRef<HTMLInputElement>(null);

 const [loading, setLoading] = useState<boolean>(true);
 const [currentPage, setCurrentPage] = useState<number>(0);
 const [rowPerPage, setRowPerPage] = useState<number>(10);
 const [searchValue, setSearchValue] = useState<string>("");
 const [errorList, setErrorList] = useState<Array<IErrorData>>([]);
 const [errorNumber, setErrorNumber] = useState<number>(0);

 useEffect(() => {
  (async () => {
   setLoading(true);
   const errorData = await dispatch(getAllErrors(0, 10));
   if (errorData) {
    setErrorList(errorData.dataset);
    setErrorNumber(errorData.totalCount);
   }
   setLoading(false);
  })();
 }, []);

 useDebounce(
  async () => {
   if (!loading) {
    setCurrentPage(0);
    const errorData = await dispatch(
     getAllErrors(currentPage, rowPerPage, searchRef?.current?.value)
    );
    if (errorData) {
     setErrorList(errorData.dataset);
     setErrorNumber(errorData.totalCount);
    }
   }
  },
  1000,
  [searchValue]
 );

 const handleOnRowsPerPageChange = async (
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
 ) => {
  const newRowsPerPage = parseInt(event.target.value, 10);
  setRowPerPage(newRowsPerPage);
  setCurrentPage(0);
  const errorData = await dispatch(getAllErrors(0, newRowsPerPage, searchRef?.current?.value));
  if (errorData) {
   setErrorList(errorData.dataset);
   setErrorNumber(errorData.totalCount);
  }
 };

 const handleOnPageChange = async (
  event: React.MouseEvent<HTMLButtonElement> | null,
  newPage: number
 ) => {
  const startIndex = newPage * rowPerPage;
  setCurrentPage(newPage);
  const errorData = await dispatch(getAllErrors(startIndex, rowPerPage, searchRef?.current?.value));
  if (errorData) {
   setErrorList(errorData.dataset);
   setErrorNumber(errorData.totalCount);
  }
 };

 return !loading ? (
  <Stack spacing={2}>
   <Stack justifyContent="space-between" direction="row">
    <Stack direction={desktopViewPort ? "row" : "column"} spacing={2} alignItems="center">
     <Typography>
      {errorNumber} <FormattedMessage id="configurator.errors.totalErrors" />
     </Typography>
     <TextField
      size="small"
      autoComplete="new-password"
      label={<FormattedMessage id="configurator.search.error" />}
      inputRef={searchRef}
      sx={{ ml: 4 }}
      InputLabelProps={{ shrink: true }}
      InputProps={{ endAdornment: <SearchIcon color="disabled" />, autoComplete: "off" }}
      onChange={({ currentTarget }) => setSearchValue(currentTarget.value)}
     />
    </Stack>
   </Stack>
   <Stack>
    <TableContainer component="div">
     <Table sx={{ minWidth: 650 }} aria-label="simple table">
      <TableHead>
       <TableRow>
        <TableCell>
         <FormattedMessage id="configurator.error.status" />
        </TableCell>
        <TableCell>
         <FormattedMessage id="configurator.error.code" />
        </TableCell>
        <TableCell>
         <FormattedMessage id="configurator.error.endpoint" />
        </TableCell>
        <TableCell>
         <FormattedMessage id="configurator.error.message" />
        </TableCell>
        <TableCell>
         <FormattedMessage id="configurator.error.user" />
        </TableCell>
       </TableRow>
      </TableHead>
      <TableBody>
       {errorList.map((row, index) => (
        <TableRow key={`isp-config-list-${index}`}>
         <TableCell component="th" scope="row">
          <Chip
           label={row.status}
           sx={{
            color: "#FF1800",
            border: "1px solid #FF1800",
            backgroundColor: "rgb(255, 24, 0, 0.2)",
            fontWeight: "bold"
           }}
           size="small"
          />
         </TableCell>
         <TableCell component="th" scope="row">
          <Typography variant="subtitle2">{row.code}</Typography>
         </TableCell>
         <TableCell component="th" scope="row">
          <Typography variant="subtitle2">{row.url}</Typography>
         </TableCell>
         <TableCell component="th" scope="row">
          <Typography variant="subtitle2">{row.message}</Typography>
         </TableCell>
         <TableCell>
          <Typography variant="subtitle2" fontWeight="bold">
           {row.personalName || "Not available"}
          </Typography>
         </TableCell>
        </TableRow>
       ))}
      </TableBody>
     </Table>
    </TableContainer>
    <TablePagination
     component="div"
     count={errorNumber}
     page={currentPage}
     rowsPerPage={rowPerPage}
     onPageChange={handleOnPageChange}
     onRowsPerPageChange={handleOnRowsPerPageChange}
    />
   </Stack>
  </Stack>
 ) : (
  <Stack>
   <CircularProgress />
  </Stack>
 );
};

export default Errors;
